// src/vatandas/pages/HomePage.js
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./HomePage.css";

import heroImage from "../assets/images/slider-image.jpg";

import {
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
  ArrowRightIcon,
  DocumentPlusIcon,
} from "@heroicons/react/24/outline";

const FAQ_ITEMS = [
  {
    q: "Başvuru oluşturmak için hangi bilgiler gerekli?",
    a: "İsim, soyisim, telefon ve başvuru konusu zorunludur. Birim seçimine göre mahalle/adres/konum veya dosya yükleme zorunlu olabilir.",
  },
  {
    q: "Başvurumu nasıl takip edebilirim?",
    a: "Başvuru oluşturduktan sonra size verilen takip numarası ile “Başvuru Takip” sayfasından durumunu görüntüleyebilirsiniz.",
  },
  {
    q: "KVKK onayı neden isteniyor?",
    a: "Başvurunuzun işlenebilmesi için kişisel verilerin işlenmesine ilişkin onay gereklidir. KVKK metnini inceleyip onaylayabilirsiniz.",
  },
  {
    q: "Dosya yükleme sınırları nedir?",
    a: "En fazla 4 dosya yükleyebilirsiniz (en fazla 2 resim + 2 PDF). Her bir dosya maksimum 10 MB olabilir.",
  },
];

function FullBleedSection({ className = "", style: styleProp, children, ...rest }) {
  const baseStyle = {
    position: "relative",
    left: "50%",
    right: "50%",
    width: "100vw",
    marginLeft: "-50vw",
    marginRight: "-50vw",
    overflowX: "clip",
  };

  return (
    <section className={className} style={{ ...baseStyle, ...(styleProp || {}) }} {...rest}>
      {children}
    </section>
  );
}

function Inner({ className = "", children }) {
  return (
    <div
      className={className}
      style={{
        maxWidth: "var(--v-page-max, 1200px)",
        margin: "0 auto",
        paddingLeft: "var(--v-page-pad, 20px)",
        paddingRight: "var(--v-page-pad, 20px)",
      }}
    >
      {children}
    </div>
  );
}

export default function VatandasHomePage() {
  const navigate = useNavigate();

  const cards = useMemo(
    () => [
      {
        key: "create",
        title: "Başvuru Yap",
        desc: "Sorununuzu hızlıca iletin, kayıt altına alınsın ve süreç başlasın.",
        icon: DocumentPlusIcon,
        to: "/basvuru-yap",
      },
      {
        key: "track",
        title: "Başvuru Takip",
        desc: "Takip numaranız ile başvurunuzun durumunu anlık olarak görüntüleyin.",
        icon: MagnifyingGlassIcon,
        to: "/basvuru-takip",
      },
      {
        key: "survey",
        title: "Memnuniyet Anketi",
        desc: "Hizmet kalitemizi artırmamıza yardımcı olacak geri bildirimi paylaşın.",
        icon: ClipboardDocumentListIcon,
        to: "/memnuniyet-anketi",
      },
    ],
    []
  );

  const go = useCallback((path) => navigate(path), [navigate]);

  // Görsel oranı: hero yüksekliği bununla hesaplanacak (üst/alt boşluk oluşmasın diye)
  const imgRef = useRef(null);
  const [heroRatio, setHeroRatio] = useState(16 / 9); // fallback

  const onHeroImageLoad = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;

    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (w > 0 && h > 0) setHeroRatio(w / h);
  }, []);

  return (
    <div className="vh3-page">
      {/* HERO — sadece görsel (yazı/buton yok) */}
      <FullBleedSection className="vh3-heroWrap" aria-label="Ana görsel alan">
        <div className="vh3-heroFrame" style={{ ["--vh3-hero-ratio"]: heroRatio }}>
          <img
            ref={imgRef}
            className="vh3-heroImg"
            src={heroImage}
            alt=""
            loading="eager"
            decoding="async"
            onLoad={onHeroImageLoad}
            draggable={false}
          />
        </div>
      </FullBleedSection>

      {/* QUICK CARDS */}
      <FullBleedSection
        id="vh3-section-cards"
        className="vh3-section vh3-section--cards"
        aria-label="Hızlı işlemler"
      >
        <Inner className="vh3-section__inner">
          <div className="vh3-section__head">
            <h2 className="vh3-section__title">Hızlı İşlemler</h2>
          </div>

          <div className="vh3-cards" role="list" aria-label="Hızlı işlem kartları">
            {cards.map((c) => {
              const Icon = c.icon;
              return (
                <button
                  key={c.key}
                  type="button"
                  className="vh3-card"
                  onClick={() => go(c.to)}
                  role="listitem"
                >
                  <div className="vh3-card__center">
                    <div className="vh3-card__iconBox" aria-hidden="true">
                      <Icon className="vh3-card__icon" aria-hidden="true" />
                    </div>

                    <div className="vh3-card__title">{c.title}</div>
                    <div className="vh3-card__desc">{c.desc}</div>
                  </div>

                  <div className="vh3-card__footer" aria-hidden="true">
                    <span className="vh3-card__footerText">Devam</span>
                    <ArrowRightIcon className="vh3-card__arrow" aria-hidden="true" />
                  </div>
                </button>
              );
            })}
          </div>
        </Inner>
      </FullBleedSection>

      {/* FAQ */}
      <FullBleedSection
        id="vh3-section-faq"
        className="vh3-section vh3-section--faq"
        aria-label="Sık sorulan sorular"
      >
        <Inner className="vh3-section__inner">
          <div className="vh3-section__head">
            <h2 className="vh3-section__title">Sık Sorulan Sorular</h2>
          </div>

          <div className="vh3-faq" role="list" aria-label="SSS listesi">
            {FAQ_ITEMS.map((item, idx) => (
              <details key={idx} className="vh3-faq__item">
                <summary className="vh3-faq__q">
                  <span className="vh3-faq__qText">{item.q}</span>
                  <span className="vh3-faq__chev" aria-hidden="true">
                    <ArrowRightIcon className="vh3-faq__chevIcon" aria-hidden="true" />
                  </span>
                </summary>
                <div className="vh3-faq__a">{item.a}</div>
              </details>
            ))}
          </div>
        </Inner>
      </FullBleedSection>
    </div>
  );
}
